<script>
import { preventDefault } from "svelte/legacy";

/* HLS Transcode: 

ffmpeg -i C:\Users\natha\Videos\example02.mp4 -c:v h264 -vf scale=720:-1 -hls_list_size 0 -hls_base_url C:\Users\natha\Videos\hls-testing\ -preset fast -tune zerolatency C:\Users\natha\Videos\hls-testing\out.m3u8

HLS Manifest Example: https://bitdash-a.akamaihd.net/content/sintel/hls/playlist.m3u8

*/
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import {
    clearMediaImportSeed,
    mediaImportState,
} from "@app/store/mediaImportStore.svelte.js";
import {
    projectState,
    updateProjectMedia,
    addMediaVersion,
} from "@app/store/projectStore.svelte.js";
import {
    updateDuration,
    updatePlayingStatus,
} from "@app/store/playerStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { uiState } from "@app/store/uiStore.svelte.js";
import { onMount } from "svelte";
import { toast } from "@zerodevx/svelte-toast";
import { v4 as uuidv4 } from "uuid";
import { fade } from "svelte/transition";
import { Circle } from "svelte-loading-spinners";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import * as m from "@app/paraglide/messages.js";
import { generatePeaks } from "@app/lib/media/generatePeaks.js";

/* CC LIB */
import tcLib from "@cclib/lib/timecode.js";
import frameRates from "@cclib/dict/frameRates.js";

const uid = uuidv4();
let projectDefaults = JSON.parse(localStorage.getItem("cc-project-defaults")) || {},
    mediaSource = $state(projectDefaults.mediaSource || "Local Storage"),
    aspectRatio = $state(projectDefaults.aspectRatio || "16/9"),
    statusMsg = $state(m.media_import_status_starting()),
    files = $state([]),
    selectedFileList = $state(),
    mediaUrl = $state(""),
    ffmpegRes,
    videoOutputPath,
    audioOutputPath,
    importing = $state(false),
    manifestFileExists = false,
    audioExtractionComplete = false,
    versionRegisteredForImport = false,
    proxyRtWorker = environment.electron
        ? new Worker("./build/workers/proxyRt.js")
        : null,
    incode = tcLib.secToTc(
        projectState.incode,
        projectState.frameRate,
        projectState.dropFrame,
    );

onMount(() => {
    applyMediaImportSeed();
    player.addEventListener("hls-manifest-loaded", hlsManifestLoaded);

    return () => {
        clearMediaImportSeed();

        if (proxyRtWorker) {
            proxyRtWorker.terminate();
        }

        player.removeEventListener("hls-manifest-loaded", hlsManifestLoaded);
    };
});

function createFileList(files) {
    if (typeof DataTransfer === "undefined") return undefined;

    try {
        const dataTransfer = new DataTransfer();
        files.forEach((file) => dataTransfer.items.add(file));
        return dataTransfer.files;
    } catch (err) {
        return undefined;
    }
}

function applyMediaImportSeed() {
    const seed = mediaImportState.seed;
    if (!seed) return;

    mediaSource = seed.mediaSource || "Local Storage";
    mediaUrl = seed.mediaUrl || "";
    files = Array.from(seed.files || []);
    selectedFileList = files.length ? createFileList(files) : undefined;
    clearMediaImportSeed();
}

function hlsManifestLoaded() {
    console.log("HLS Manifest Loaded");
    manifestFileExists = true;
    closeModal();
}

function closeModal() {
    console.log(
        "Close Modal Called",
        audioExtractionComplete,
        manifestFileExists,
    );

    updatePlayingStatus(false);
    if (audioExtractionComplete && manifestFileExists) {
        statusMsg = m.media_import_status_complete();

        if (
            environment.electron &&
            getMediaIdentity(projectState.media) &&
            !versionRegisteredForImport
        ) {
            addMediaVersion(projectState.media, projectState.media.name);
            versionRegisteredForImport = true;
        }

        setTimeout(() => {
            uiState.timeline = true;
            hideModal();
        }, 1000);
    }
}

function getMediaIdentity(mediaObj) {
    return mediaObj?.localPath || mediaObj?.path || mediaObj?.name || "";
}

async function extractPeaksForImport() {
    try {
        const peaksPath = await generatePeaks(projectState.media, {
            onProgress: (result) => {
                statusMsg = result;
            },
        });

        projectState.media.peaksPath = peaksPath;
        projectState.media.useFallback = false;
        toast.push(m.media_import_completed_toast(), {
            classes: ["toast-success"],
        });
    } catch (err) {
        projectState.media.peaksPath = "";
        projectState.media.peaksData = [[0], [0]];
        toast.push(`${err.message || m.media_import_audio_extraction_complete()}`, {
            classes: ["toast-danger"],
        });
    } finally {
        audioExtractionComplete = true;
        closeModal();
    }
}

function alertUserBoolean(msg) {
    let response = Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.precheck,
        titleText: m.media_import_metadata_scan_title(),
        text: msg,
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.yes(),
        denyButtonText: m.no(),
        allowOutsideClick: false,
        allowEscapeKey: false,
    }).then((result) => {
        if (result.isConfirmed) {
            return true;
        } else {
            return false;
        }
    });

    return response;
}

async function getFileMetadata(filePath, ffmpegFullPath) {
    try {
        const exec = window.exec;
        return await exec(
            `"${ffmpegFullPath.replace("app.asar", "app.asar.unpacked")}" -y -i "${filePath}"`,
        );
    } catch (err) {
        return err.message;
    }
}

function getMediaIncode(ffmpegOutput) {
    let matches = ffmpegOutput.match(
        /(?<=timecode\s+:\s+)\d\d:\d\d:\d\d(:|;)\d\d/,
    );
    let mediaIncode = matches ? matches[0].replace(";", ":") : false;

    console.log("Media incode detected: " + mediaIncode);
    return mediaIncode == "null" ? false : mediaIncode;
}

function getMediaFrameRate(ffmpegOutput) {
    let matches = ffmpegOutput.match(/\d+\.\d+(?= fps)|\d+(?= fps)/);
    let mediaFrameRate = matches ? matches[0] : false;

    console.log("Media frame rate detected: " + mediaFrameRate);
    let fr = frameRates.frameRateMapping[mediaFrameRate];
    return fr;
}

function handleFileInput(event) {
    selectedFileList = event.currentTarget.files;
    files = Array.from(selectedFileList || []);
}

async function importMedia() {
    importing = true;
    versionRegisteredForImport = false;
    player.src = '';
    uiState.timeline = false;
    updateDuration(false);

    //Save Media Source and Aspect Ratio to Local Storage
    projectDefaults.mediaSource = mediaSource;
    projectDefaults.aspectRatio = aspectRatio;
    localStorage.setItem(
        "cc-project-defaults",
        JSON.stringify(projectDefaults),
    );

    updateProjectMedia({
        projectName: projectState.name,
        storage: mediaSource,
        mediaUrl: mediaUrl,
        files: files,
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame,
        aspectRatio: aspectRatio,
    });

    if (environment.electron && files && files.length > 0) {
        projectState.media.localPath = await window.webUtils.getPathForFile(files[0]);
    }

    let mediaType = "video/mp4";

    switch (mediaSource) {
        case "Vimeo":
            player.src = projectState.media.path;
            manifestFileExists = true;
            audioExtractionComplete = true;

            break;
        case "YouTube":
            player.src = projectState.media.path;
            manifestFileExists = true;
            audioExtractionComplete = true;

            break;
        case "HLS Manifest":
            audioExtractionComplete = true;
            player.src = projectState.media.path;

            break;
        case "Proxy RT":
            videoOutputPath = os.tmpdir() + path.sep + uid + ".mp4";
            audioOutputPath = os.tmpdir() + path.sep + uid + ".json";

            projectState.media.path = videoOutputPath;

            ffmpegRes = await getFileMetadata(
                projectState.media.localPath,
                window.ffmpegPath,
            );

            projectState.media.info = {
                incode: getMediaIncode(ffmpegRes),
                frameRate: getMediaFrameRate(ffmpegRes),
                duration: getMediaDuration(ffmpegRes),
            };

            /* -------------auto detect frame rate start------------- */
            if (
                projectState.media.info.frameRate &&
                projectState.media.info.frameRate != projectState.frameRate
            ) {
                if (
                    await alertUserBoolean(
                        m.media_import_update_frame_rate_prompt({
                            rate: projectState.media.info.frameRate,
                        }),
                    )
                ) {
                    projectState.frameRate = projectState.media.info.frameRate;
                    projectState.dropFrame =
                        projectState.media.info.frameRate === 29.97 ||
                        projectState.media.info.frameRate === 59.94
                            ? true
                            : false;
                }
            }
            /* -------------auto detect frame rate end------------- */

            /* -------------auto detect incode start------------- */
            if (
                projectState.media.info.incode &&
                projectState.media.info.incode != incode
            ) {
                if (
                    await alertUserBoolean(
                        m.media_import_update_incode_prompt({
                            timecode: projectState.media.info.incode,
                        }),
                    )
                ) {
                    let multiplier = projectState.frameRate === 23.976 || (projectState.frameRate === 29.97 && !projectState.dropFrame) ? 24/23.976 : 1;

                    incode = projectState.media.info.incode;
                    projectState.incode = parseFloat(((tcLib.tcToSec(incode, projectState.frameRate, projectState.dropFrame, true))*multiplier).toFixed(3));

                    console.log("INCODE INFORMATION")
                    console.log(incode, projectState.incode, multiplier);
                }
            }
            /* -------------auto detect incode end------------- */
            console.log("---------------------");
            console.log("DURATION: " + projectState.media.info.duration);
            console.log("---------------------");

            extractPeaksForImport();

            proxyRtWorker.postMessage({
                inputPath: projectState.media.localPath,
                outputPath: videoOutputPath,
                ffmpegPath: window.ffmpegPath,
                tmpDir: os.tmpdir(),
                pathSep: path.sep,
            });

            proxyRtWorker.onmessage = async (msg) => {
                //console.log(msg);
                if (msg.data.status === "in_progress") {
                    statusMsg = msg.data.status_msg;
                } else if (
                    msg.data.status === "done" &&
                    msg.data.result === "passed"
                ) {
                    manifestFileExists = true;
                    statusMsg = msg.data.status_msg;
                    player.src = videoOutputPath;
                    closeModal();
                } else if (
                    msg.data.status === "done" &&
                    msg.data.result === "error"
                ) {
                    manifestFileExists = true;
                    toast.push(`${msg.data.error}`, {
                        classes: ["toast-danger"],
                    });

                    closeModal();
                }
            };

            break;
        case "Cloud Storage":
            /* Cloud Storage*/
            if (projectState.media.type === "video/quicktime") {
                mediaType = "video/mp4";
            } else if (projectState.media.type === "video/x-matroska") {
                mediaType = "video/mkv";
            } else {
                mediaType = projectState.media.type;
            }

            //Get file size of url
            let fileSize = await getFileSize(projectState.media.path);
            if (fileSize) {
                const sizeInBytes = parseInt(fileSize);
                const sizeInKilobytes = sizeInBytes / 1024;
                const sizeInMegabytes = sizeInKilobytes / 1024;
                if (sizeInMegabytes < 512) {
                    projectState.media.useFallback = false;
                }
            }

            player.src = { src: projectState.media.path, type: mediaType };

            /* Update Flags */
            manifestFileExists = true;
            audioExtractionComplete = true;

            break;
        default:
            /* Local Storage */
            manifestFileExists = true;
            mediaType = projectState.media.type === "video/quicktime" ? "video/mp4" : projectState.media.type;
            /* We don't need to convert this to an Object URL because it already does this */
            player.src = { src: environment.electron ? projectState.media.localPath : projectState.media.path, type: mediaType };

            if (environment.electron) {
                audioOutputPath = os.tmpdir() + path.sep + uid + ".json";
                //console.log(window.ffmpegPath);
                ffmpegRes = await getFileMetadata(
                    projectState.media.localPath,
                    window.ffmpegPath,
                );
                console.log("-----FILE INFO-----");
                console.log(ffmpegRes);
                console.log("-------------------");
                projectState.media.info = {
                    incode: getMediaIncode(ffmpegRes),
                    frameRate: getMediaFrameRate(ffmpegRes),
                    duration: getMediaDuration(ffmpegRes),
                };
                /* Auto detect frame rate */
                if (
                    projectState.media.info.frameRate &&
                    projectState.media.info.frameRate != projectState.frameRate
                ) {
                    if (
                        await alertUserBoolean(
                            m.media_import_update_frame_rate_prompt({
                                rate: projectState.media.info.frameRate,
                            }),
                        )
                    ) {
                        projectState.frameRate =
                            projectState.media.info.frameRate;
                        projectState.dropFrame =
                            projectState.media.info.frameRate === 29.97 ||
                            projectState.media.info.frameRate === 59.94
                                ? true
                                : false;
                    }
                }
                /* -------------auto detect frame rate------------- */

                /* Auto detect incode */
                if (
                    projectState.media.info.incode &&
                    projectState.media.info.incode != incode
                ) {
                    if (
                        await alertUserBoolean(
                            m.media_import_update_incode_prompt({
                                timecode: projectState.media.info.incode,
                            }),
                        )
                    ) {
                        let multiplier = projectState.frameRate === 23.976 || (projectState.frameRate === 29.97 && !projectState.dropFrame) ? 24/23.976 : 1;

                        incode = projectState.media.info.incode;
                        projectState.incode = parseFloat(((tcLib.tcToSec(incode, projectState.frameRate, projectState.dropFrame, true))*multiplier).toFixed(3));

                        console.log("INCODE INFORMATION")
                        console.log(incode, projectState.incode, multiplier);
                    }
                }
                /* -------------auto detect incode------------- */
                console.log("---------------------");
                console.log("DURATION: " + projectState.media.info.duration);
                console.log("---------------------");

                extractPeaksForImport();
            } else {
                /* Running in browser */
                if (files && files.length > 0 && files[0].size < 524288000) {
                    // 500MB (524288000 bytes)
                    projectState.media.useFallback = false;
                }

                audioExtractionComplete = true;
            }
    }

    closeModal();
}

function getMediaDuration(ffmpegOutput) {
    /*  console.log("GETTING MEDIA DURATION");
    console.log("FFMPEG OUTPUT:");
    console.log(ffmpegOutput); */
    let matches = ffmpegOutput.match(/(?<=Duration:\s)\d\d:\d\d:\d\d\.\d\d/);
    let mediaDuration = matches ? matches[0] : false;

    console.log(mediaDuration);
    if (mediaDuration) {
        mediaDuration = tcLib.tcMsToSec(mediaDuration);
        console.log("media duration in seconds: " + mediaDuration);
    }

    return mediaDuration;
}

async function getFileSize() {
    try {
        const response = await fetch(projectState.media.path, {
            method: "HEAD",
        });
        const fileSize = response.headers.get("content-length");
        return fileSize;
    } catch (err) {
        return false;
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'mediaImport' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="mediaImportTitle"
    aria-describedby="mediaImportDescription"
    tabindex="-1"
    id="MediaImportModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="mediaImportTitle">
                    {m.media_import_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    id="closeMediaImportBtn"
                    aria-label={m.media_import_close_label()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p id="mediaImportDescription" class="sr-only">
                    {m.media_import_description()}
                </p>
                <form onsubmit={preventDefault(importMedia)}>
                    <div class="mb-3">
                        <label class="form-label" for="mediaSourceSelect">
                            {m.media_import_source_label()}
                        </label>
                        <select
                            id="mediaSourceSelect"
                            class="form-select"
                            bind:value={mediaSource}
                            aria-describedby="mediaSourceHelp">
                            <option value="Local Storage">
                                {m.media_import_source_local()}
                            </option>
                            {#if environment.online}
                                <option value="YouTube">
                                    {m.media_import_source_youtube()}
                                </option>
                                <option value="Vimeo">
                                    {m.media_import_source_vimeo()}
                                </option>
                                <option value="Cloud Storage">
                                    {m.media_import_source_cloud()}
                                </option>
                                <option value="HLS Manifest">
                                    {m.media_import_source_hls()}
                                </option>
                            {/if}
                            {#if environment.electron}
                                <option value="Proxy RT">
                                    {m.media_import_source_proxy_rt()}
                                </option>
                            {/if}
                        </select>
                        <div id="mediaSourceHelp" class="form-text">
                            {m.media_import_source_help()}
                        </div>
                    </div>
                    <div class="mb-3">
                        {#if mediaSource === "Local Storage"}
                            <p class="mb-2" id="mediaLocationLabel">
                                {m.media_import_location_label()}
                            </p>
                            <input
                                type="file"
                                class="form-control"
                                id="localFileInput"
                                accept="video/*,audio/*"
                                bind:files={selectedFileList}
                                onchange={handleFileInput}
                                aria-labelledby="mediaLocationLabel" />
                        {:else if mediaSource === "HLS Manifest"}
                            <label class="form-label" for="HlsManifestUrl">
                                {m.media_import_hls_label()}
                            </label>
                            <input
                                bind:value={mediaUrl}
                                type="url"
                                class="form-control"
                                id="HlsManifestUrl"
                                placeholder={m.media_import_hls_placeholder()}
                                aria-describedby="hlsUrlHelp" />
                            <div id="hlsUrlHelp" class="form-text">
                                {m.media_import_hls_help()}
                            </div>
                        {:else if mediaSource === "Proxy RT"}
                            <p class="mb-2">{m.media_import_location_label()}</p>
                            <input
                                type="file"
                                class="form-control"
                                accept=".mxf, .ixf, .aaf, .imf, .gxf, .mov, .mp4, .mpeg, .mpeg2, .mpg, .avi, .mkv, .wav, .mp3, .flac"
                                onchange={handleFileInput} />
                        {:else if mediaSource === "YouTube"}
                            <label class="form-label" for="YouTubeSourceUrl">
                                {m.media_import_youtube_label()}
                            </label>
                            <input
                                bind:value={mediaUrl}
                                type="url"
                                class="form-control"
                                id="YouTubeSourceUrl"
                                placeholder={m.media_import_video_url_placeholder()} />
                        {:else if mediaSource === "Vimeo"}
                            <label class="form-label" for="VimeoSourceUrl">
                                {m.media_import_vimeo_label()}
                            </label>
                            <input
                                bind:value={mediaUrl}
                                type="url"
                                class="form-control"
                                id="VimeoSourceUrl"
                                placeholder={m.media_import_video_url_placeholder()} />
                        {:else}
                            <label class="form-label" for="CloudSourceUrl">
                                {m.media_import_cloud_label()}
                            </label>
                            <input
                                bind:value={mediaUrl}
                                type="url"
                                class="form-control"
                                id="CloudSourceUrl"
                                placeholder={m.media_import_cloud_placeholder()} />
                        {/if}
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="aspectRatioSelect">
                            {m.media_import_aspect_ratio_label()}
                        </label>
                        <select
                            class="form-select"
                            id="aspectRatioSelect"
                            bind:value={aspectRatio}
                            aria-describedby="aspectRatioHelp">
                            <option value="16/9">16:9</option>
                            <option value="4/3">4:3</option>
                            <option value="1/1">1:1</option>
                            <option value="9/16">9:16</option>
                            <option value="21/9">21:9</option>
                            <option value="143/100">1.43:1</option>
                            <option value="185/100">1.85:1</option>
                            <option value="235/100">2.35:1</option>
                            <option value="239/100">2.39:1</option>
                            <option value="276/100">2.76:1</option>
                        </select>
                        <div id="aspectRatioHelp" class="form-text">
                            {m.media_import_aspect_ratio_help()}
                        </div>
                    </div>
                </form>
            </div>
            <div class="modal-footer">
                {#if importing}
                    <p
                        class="text-muted small"
                        role="status"
                        aria-live="polite">
                        {m.media_import_loading_label()} | {statusMsg}
                    </p>
                    <Circle size="30" color="#1eb4b2" unit="px" duration="1s" />
                {/if}
                <button
                    class="btn btn-primary"
                    type="button"
                    id="importMediaBtn"
                    disabled={importing ||
                        (mediaSource === "Local Storage" &&
                            files.length === 0) ||
                        (mediaSource === "Proxy RT" && files.length === 0) ||
                        (mediaSource !== "Local Storage" &&
                            mediaSource !== "Proxy RT" &&
                            mediaUrl.length === 0)}
                    aria-label={m.media_import_action_label()}
                    onclick={importMedia}>{m.media_import_action_label()}</button>
            </div>
        </div>
    </div>
</div>
